
import React from 'react';
import {
  Package,
  User,
  Phone,
  MapPin,
  CheckCircle,
  X,
  Eye,
  Clock,
  Smartphone,
  QrCode,
  AlertCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './NotificationDropdown.css';

const NotificationDropdown = ({
  show,
  notificationCount,
  pendingNotifications,
  cancelledNotifications = [],
  onAccept,
  onReject,
  onViewDetails,
  onViewAll,
  onClose,
  // ✅ NEW: callback from AppLayout
  onClearNotification,
}) => {
  const navigate = useNavigate();

  if (!show) return null;

  // ==================== FORMAT ORDER FOR DISPLAY ====================
  const formatOrderForDisplay = (order) => {
    return {
      id: `#ORD-${order._id?.slice(-6).toUpperCase()}`,
      _id: order._id,
      customer: order.name || 'Customer',
      phone: order.phoneNumber || 'N/A',
      delivery: order.address || 'Delivery Address',
      amount: parseFloat(order.amount) || 0,
      status: order.status,
      items: order.products?.length || 0,
      createdAt: order.createdAt || new Date(),
      cancellationReason: order.cancellationReason || 'No reason provided',
      cancelledAt: order.cancelledAt || new Date(),
      cancelledBy: order.cancelledBy || 'Customer',
      paymentMethod: order.paymentMethod,
    };
  };

  // ==================== FORMAT TIME AGO ====================
  const getTimeAgo = (date) => {
    if (!date) return 'Just now';

    const now = new Date();
    const orderDate = new Date(date);
    const diffInMinutes = Math.floor((now - orderDate) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60)
      return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''} ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  // ==================== GET STATUS DISPLAY ====================
  const getStatusDisplay = (status) => {
    const statusMap = {
      confirmed: { emoji: '🔔', text: 'New Order', color: '#3b82f6' },
      shipped: { emoji: '🚚', text: 'Picked Up', color: '#8b5cf6' },
      out_for_delivery: { emoji: '🚀', text: 'Out for Delivery', color: '#10b981' },
      cancelled: { emoji: '❌', text: 'Cancelled', color: '#ef4444' },
    };
    return statusMap[status] || { emoji: '📦', text: status, color: '#6b7280' };
  };

  // ==================== HANDLE ACCEPT ====================
  const handleAccept = (orderId) => {
    onAccept(orderId);
  };

  // ==================== HANDLE REJECT ====================
  const handleReject = (orderId) => {
    onReject(orderId);
  };

  // ==================== HANDLE VIEW DETAILS ====================
  const handleViewDetails = (order) => {
    if (onViewDetails) {
      onViewDetails(order);
    }
  };

  // ==================== HANDLE VERIFY OTP (NAVIGATE TO HISTORY) ====================
  const handleVerifyOTP = (order) => {
    onClose();
    navigate('/history', {
      state: {
        selectedOrder: order,
        action: 'verifyOTP',
        openOTPModal: true,
      },
    });
  };

  // ==================== HANDLE SHOW QR (NAVIGATE TO HISTORY) ====================
  const handleShowQR = (order) => {
    onClose();
    navigate('/history', {
      state: {
        selectedOrder: order,
        action: 'showQR',
        openPaymentScanner: true,
      },
    });
  };

  // ==================== GET ACTION BUTTON BASED ON ORDER STATUS ====================
  const getActionButton = (order) => {
    const status = order.status;

    switch (status) {
      case 'confirmed':
        return (
          <button
            className="btn-accept-small"
            onClick={() => handleAccept(order._id)}
            title="Accept & Pick Up Order"
            aria-label={`Accept order ${order._id?.slice(-6).toUpperCase()}`}
          >
            <CheckCircle size={16} /> Accept
          </button>
        );

      case 'shipped':
        return (
          <button
            className="btn-verify-small"
            onClick={() => handleVerifyOTP(order)}
            title="Verify OTP & Continue"
            aria-label={`Verify OTP for order ${order._id?.slice(-6).toUpperCase()}`}
          >
            <Smartphone size={16} /> Verify OTP
          </button>
        );

      case 'out_for_delivery':
        if (order.paymentMethod === 'Cash on Delivery') {
          return (
            <button
              className="btn-payment-small"
              onClick={() => handleShowQR(order)}
              title="Show Payment QR"
              aria-label={`Show payment QR for order ${order._id?.slice(-6).toUpperCase()}`}
            >
              <QrCode size={16} /> Show QR
            </button>
          );
        }
        return (
          <button
            className="btn-deliver-small"
            onClick={() => handleViewDetails(order)}
            title="Mark as Delivered"
            aria-label={`Mark order ${order._id?.slice(-6).toUpperCase()} as delivered`}
          >
            <CheckCircle size={16} /> Mark Delivered
          </button>
        );

      case 'cancelled':
        return (
          <button
            className="btn-cancelled-small"
            disabled
            title="Order Cancelled"
            aria-label={`Order ${order._id?.slice(-6).toUpperCase()} cancelled`}
          >
            <AlertCircle size={16} /> Cancelled
          </button>
        );

      default:
        return null;
    }
  };

  // ✅ Combine pending and cancelled notifications
  const allNotifications = [
    ...pendingNotifications.map((order) => ({ ...order, type: 'pending' })),
    ...cancelledNotifications.map((order) => ({ ...order, type: 'cancelled' })),
  ].sort((a, b) => {
    const dateA = new Date(a.cancelledAt || a.createdAt || 0);
    const dateB = new Date(b.cancelledAt || b.createdAt || 0);
    return dateB - dateA;
  });


  
  // ==================== RENDER ====================
  return (
    <>
      {/* Backdrop overlay */}
      <div className="notification-backdrop" onClick={onClose}></div>

      {/* Dropdown */}
      <div className="notification-dropdown">
        {/* ==================== HEADER ==================== */}
        <div className="notification-dropdown-header">
          <div className="header-left">
            <h3>Notifications</h3>
            {notificationCount > 0 && (
              <span
                className="notification-count-badge"
                aria-label={`${notificationCount} new notifications`}
              >
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </div>
          <button
            className="close-dropdown-btn"
            onClick={onClose}
            aria-label="Close notifications"
          >
            <X size={20} />
          </button>
        </div>

        {/* ==================== CONTENT ==================== */}
        <div className="notification-dropdown-content">
          {allNotifications.length === 0 ? (
            <div className="empty-notifications">
              <Package size={48} color="#ccc" />
              <p className="empty-title">No New Notifications</p>
              <p className="empty-subtitle">You're all caught up! 🎉</p>
            </div>
          ) : (
            allNotifications.map((order, index) => {
              const displayOrder = formatOrderForDisplay(order);
              const statusDisplay = getStatusDisplay(order.status);
              const isCancelled =
                order.type === 'cancelled' || order.status === 'cancelled';



              return (
                <div
                  key={order._id}
                  className={`notification-item ${
                    isCancelled ? 'cancelled-order' : ''
                  }`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {/* Order Header */}
                  <div className="notification-item-header">
                    <div className="header-info">
                      <span className="notification-order-id">
                        {displayOrder.id}
                      </span>
                      <span className="notification-time">
                        <Clock size={12} />
                        {getTimeAgo(
                          isCancelled ? order.cancelledAt : order.createdAt
                        )}
                      </span>
                    </div>
                    <span className="notification-order-amount">
                      ₹{displayOrder.amount.toFixed(2)}
                    </span>
                  </div>

                  {/* Status Badge */}
                  <div
                    className="notification-status-badge"
                    style={{ borderLeftColor: statusDisplay.color }}
                  >
                    <span className="status-emoji">{statusDisplay.emoji}</span>
                    <span className="status-text">{statusDisplay.text}</span>
                  </div>

                  {/* Cancellation Alert */}
                  {isCancelled && (
                    <div className="cancellation-alert">
                      <AlertCircle size={14} />
                      <div className="cancellation-info">
                        <span className="cancellation-text">
                          Cancelled by:{' '}
                          <strong>{displayOrder.cancelledBy}</strong>
                        </span>
                        <span className="cancellation-reason">
                          Reason: {displayOrder.cancellationReason}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Order Body */}
                  <div className="notification-item-body">
                    <div className="notification-info-row">
                      <User size={14} className="info-icon" />
                      <span className="info-text">{displayOrder.customer}</span>
                    </div>
                    <div className="notification-info-row">
                      <Phone size={14} className="info-icon" />
                      <span className="info-text">{displayOrder.phone}</span>
                    </div>
                    <div className="notification-info-row">
                      <MapPin size={14} className="info-icon" />
                      <span
                        className="info-text address-ellipsis"
                        title={displayOrder.delivery}
                      >
                        {displayOrder.delivery}
                      </span>
                    </div>
                    <div className="notification-info-row">
                      <Package size={14} className="info-icon" />
                      <span className="info-text">
                        {displayOrder.items} item(s)
                      </span>
                    </div>

                    {displayOrder.paymentMethod === 'Cash on Delivery' &&
                      !isCancelled && (
                        <div className="cod-badge-small">
                          💵 Cash on Delivery
                        </div>
                      )}
                  </div>

                  {/* Order Actions */}
                  <div className="notification-item-actions">
                    {!isCancelled && getActionButton(order)}

                    {order.status === 'confirmed' && !isCancelled && (
                      <button
                        className="btn-reject-small"
                        onClick={() => handleReject(order._id)}
                        title="Reject Order"
                        aria-label={`Reject order ${displayOrder.id}`}
                      >
                        <X size={16} /> Reject
                      </button>
                    )}
        
                    {/* ✅ NEW: Clear single notification */}
                    {onClearNotification && (
                      <button
                        className="btn-clear-small"
                        onClick={() => onClearNotification(order._id)}
                        title="Clear notification"
                        aria-label={`Clear notification for order ${displayOrder.id}`}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              );

              
            })
          )}
        </div>

        {/* ==================== FOOTER ==================== */}
        {allNotifications.length > 0 && (
          <div className="notification-dropdown-footer">
            <button
              className="view-all-btn"
              onClick={onViewAll}
              aria-label="View all orders"
            >
              View All Orders →
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationDropdown;

